/**
 * CommitmentEngine
 *
 * Accountability tracker per entity.
 * - Shows previous commitments for the current user
 * - Allows marking fulfilled / missed
 * - Form to add a new commitment (with optional deadline)
 * - Aggregated fulfillment stats
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  CheckIcon,
  CloseIcon,
  GoalIcon,
  AddIcon,
  HourglassIcon,
  SuccessIcon,
  WarningIcon,
  Loader,
  ICON_STYLES,
} from '../../components/icons';
import { Timestamp } from 'firebase/firestore';
import {
  getPreviousCommitmentsForUser,
  saveCommitment,
  updateCommitmentStatus,
} from './structuredGrowthService';
import type { Commitment, CommitmentStatus, StructuredGrowthEntityType } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CommitmentEngineProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  userId: string;
  userName: string;
  meetingId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusColor(status: CommitmentStatus): string {
  switch (status) {
    case 'fulfilled': return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    case 'missed':    return 'text-red-500 bg-red-50 border-red-100';
    default:          return 'text-amber-600 bg-amber-50 border-amber-100';
  }
}

function statusLabel(status: CommitmentStatus): string {
  switch (status) {
    case 'fulfilled': return 'Nagekomen';
    case 'missed':    return 'Niet nagekomen';
    default:          return 'Openstaand';
  }
}

function formatDeadline(ts: Commitment['deadline']): string {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date();
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

// ---------------------------------------------------------------------------
// Fulfillment rate badge
// ---------------------------------------------------------------------------

const FulfillmentBadge = memo(function FulfillmentBadge({
  commitments,
}: {
  commitments: Commitment[];
}) {
  if (commitments.length === 0) return null;
  const fulfilled = commitments.filter((c) => c.status === 'fulfilled').length;
  const rate = Math.round((fulfilled / commitments.length) * 100);
  const color =
    rate >= 80 ? 'text-emerald-700 bg-emerald-50' :
    rate >= 50 ? 'text-amber-700 bg-amber-50' :
    'text-red-700 bg-red-50';

  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>
      {rate}% nagekomen
    </span>
  );
});

// ---------------------------------------------------------------------------
// Commitment row
// ---------------------------------------------------------------------------

const CommitmentRow = memo(function CommitmentRow({
  commitment,
  onStatusChange,
}: {
  commitment: Commitment;
  onStatusChange: (id: string, status: CommitmentStatus) => void;
}) {
  const isPending = commitment.status === 'pending';

  return (
    <div className={`rounded-xl border p-4 ${statusColor(commitment.status)}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {commitment.status === 'fulfilled' ? (
            <SuccessIcon size={16} weight="fill" className="text-emerald-500" />
          ) : commitment.status === 'missed' ? (
            <WarningIcon size={16} weight="fill" className="text-red-400" />
          ) : (
            <GoalIcon size={16} weight="regular" className="text-amber-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-snug">
            {commitment.commitmentText}
          </p>
          {commitment.deadline && (
            <div className="flex items-center gap-1 mt-1">
              <HourglassIcon size={11} weight="regular" className="text-gray-400" />
              <span className="text-xs text-gray-500">
                Deadline: {formatDeadline(commitment.deadline)}
              </span>
            </div>
          )}
          <span className={`inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wide ${statusColor(commitment.status)} px-1.5 py-0.5 rounded border`}>
            {statusLabel(commitment.status)}
          </span>
        </div>

        {/* Mark fulfilled / missed */}
        {isPending && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onStatusChange(commitment.id, 'fulfilled')}
              className="w-7 h-7 rounded-lg flex items-center justify-center bg-white border border-emerald-200 text-emerald-500 hover:bg-emerald-50 transition-colors"
              title="Nagekomen"
            >
              <CheckIcon size={13} weight="bold" />
            </button>
            <button
              onClick={() => onStatusChange(commitment.id, 'missed')}
              className="w-7 h-7 rounded-lg flex items-center justify-center bg-white border border-red-200 text-red-400 hover:bg-red-50 transition-colors"
              title="Niet nagekomen"
            >
              <CloseIcon size={13} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CommitmentEngine: React.FC<CommitmentEngineProps> = ({
  entityId,
  entityType,
  userId,
  userName,
  meetingId,
}) => {
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPreviousCommitmentsForUser(entityId, userId, 10);
      setCommitments(data);
    } finally {
      setLoading(false);
    }
  }, [entityId, userId]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = useCallback(async (id: string, status: CommitmentStatus) => {
    setCommitments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status } : c)),
    );
    try {
      await updateCommitmentStatus(id, status);
    } catch {
      // Revert on error
      load();
    }
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const deadlineTs = deadline
        ? Timestamp.fromDate(new Date(deadline))
        : null;

      await saveCommitment({
        entityId,
        userId,
        userName,
        meetingId,
        commitmentText: text.trim(),
        deadline: deadlineTs,
        status: 'pending',
      });
      setText('');
      setDeadline('');
      setShowForm(false);
      await load();
    } finally {
      setSaving(false);
    }
  }, [text, deadline, entityId, userId, userName, meetingId, load]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <GoalIcon size={16} weight="regular" className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Commitments</span>
        </div>
        <div className="flex items-center gap-2">
          <FulfillmentBadge commitments={commitments} />
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="h-7 px-2.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1 transition-colors"
            >
              <AddIcon size={12} weight="bold" />
              <span>Nieuw</span>
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Maak een concrete afspraak met jezelf na elke sessie. Bij de volgende meeting zie je jouw vorige commitment terug en kun je aangeven of je het hebt nagekomen.
      </p>

      {/* New commitment form */}
      {showForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Beschrijf je commitment voor de volgende sessie..."
            rows={2}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none bg-white"
          />
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 flex-1">
              <HourglassIcon size={13} weight="regular" className="text-gray-400 shrink-0" />
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-900 text-gray-700"
              />
            </div>
            <button
              onClick={handleSave}
              disabled={saving || !text.trim()}
              className="h-8 px-3 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              <CheckIcon size={12} weight="bold" />
              <span>{saving ? 'Opslaan...' : 'Opslaan'}</span>
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="h-8 px-2 text-xs text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <CloseIcon size={14} weight="regular" />
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader size={18} strokeWidth={1.5} className="animate-spin text-gray-300" />
        </div>
      ) : commitments.length === 0 ? (
        <div className="text-center py-8">
          <GoalIcon size={20} weight="regular" className="text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-400">Nog geen commitments.</p>
          <p className="text-xs text-gray-400">Voeg er een toe na je volgende sessie.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {commitments.map((c) => (
            <CommitmentRow
              key={c.id}
              commitment={c}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(CommitmentEngine);
